import { useRef, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { Icon, RetryImage, Tooltip } from '@/components/ui';
import { VideoPlayBadge } from '@/components/ui/VideoPlayBadge';
import { HoverCard } from '@/components/ui/HoverCard';
import { TextPostTile } from '@/components/ui/TextPostTile';
import { DashboardPostPopover } from '@/components/dashboard/DashboardPostPopover';
import { PlatformBrandIcon } from '@/components/PostComposer/sections/preview/components/PlatformBrandIcon';
import { ICONS } from '@/constants/icons';
import type { Post, PostVariationSummary } from '@/services/posts';
import type { PostVariation } from '@/types/variations';
import { releaseItemKey } from '@/types/releaseSections';
import { cn } from '@/utils/cn';
import './releasedShelf.css';

export interface ReleasedShelfItem {
  post: Post;
  variation?: PostVariationSummary;
  releasedAt: string;
}

export const toReleasedShelfVariation = (post: Post, variation: PostVariationSummary): PostVariation => ({
  id: variation.id,
  postId: post.id,
  title: variation.title,
  caption: variation.caption ?? post.caption,
  postMedia: variation.postMedia ?? post.postMedia,
  createdAt: variation.createdAt ?? post.createdAt,
  updatedAt: variation.updatedAt ?? post.updatedAt,
  post: {
    id: post.id,
    caption: post.caption,
    labels: post.labels,
    postMedia: post.postMedia,
    postChannels: post.postChannels,
  },
});

const ShelfTile = ({
  item,
  onSchedule,
  onDetails,
  leaving,
}: {
  item: ReleasedShelfItem;
  onSchedule: (item: ReleasedShelfItem) => void;
  onDetails: (item: ReleasedShelfItem) => void;
  leaving: boolean;
}) => {
  const { t } = useTranslation('calendar');
  const { post, variation } = item;
  const key = releaseItemKey(post.id, variation?.id ?? null);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: `release-shelf-${key}`,
    data: variation
      ? { type: 'release-shelf-variation', variation: toReleasedShelfVariation(post, variation) }
      : { type: 'release-shelf-post', post },
  });
  const media = (variation?.postMedia?.length ? variation.postMedia : post.postMedia)?.[0];
  const image = media?.type === 'VIDEO' ? media.thumbnailUrl : media?.thumbnailUrl || media?.originalUrl;
  const caption = variation?.caption || variation?.title || post.caption || t('release_shelf_untitled');
  const platforms = [...new Set(post.postChannels?.map((pc) => pc.channel.platform.toLowerCase()) ?? [])].slice(0, 3);
  const touchStartRef = useRef<{ x: number; y: number; time: number; moved: boolean } | null>(null);
  const suppressClickUntilRef = useRef(0);

  return (
    <div
      ref={setNodeRef}
      data-testid="release-shelf-tile"
      data-item-key={key}
      className={cn(
        'release-shelf-tile group relative aspect-square w-[120px] shrink-0 overflow-hidden rounded-xl border border-primary-200 bg-primary-50 shadow-sm sm:w-[190px]',
        isDragging && 'opacity-50',
        leaving && 'release-shelf-tile--leaving'
      )}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        {...listeners}
        {...attributes}
        onTouchStartCapture={(event) => {
          const touch = event.touches[0];
          touchStartRef.current = touch
            ? { x: touch.clientX, y: touch.clientY, time: Date.now(), moved: false }
            : null;
        }}
        onTouchMoveCapture={(event) => {
          const start = touchStartRef.current;
          const touch = event.touches[0];
          if (start && touch && Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 10) {
            start.moved = true;
          }
        }}
        onTouchEndCapture={() => {
          const start = touchStartRef.current;
          if (start && (start.moved || Date.now() - start.time >= 250)) {
            suppressClickUntilRef.current = Date.now() + 400;
          }
          touchStartRef.current = null;
        }}
        onTouchCancelCapture={() => {
          suppressClickUntilRef.current = Date.now() + 400;
          touchStartRef.current = null;
        }}
        onClick={() => {
          if (isDragging || Date.now() < suppressClickUntilRef.current) return;
          onSchedule(item);
        }}
        className="absolute inset-0 overflow-hidden text-left focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary-500"
        aria-label={t('release_shelf_pick_day_for', { title: caption })}
      >
        {image ? (
          <RetryImage src={image} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-primary-500">
            <Icon name={media?.type === 'VIDEO' ? ICONS.VIDEO : media?.type === 'PDF' ? ICONS.DOCUMENT : ICONS.IMAGE} size="lg" ariaHidden />
          </span>
        )}
        {media?.type === 'VIDEO' && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <VideoPlayBadge />
          </span>
        )}
        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/65 to-transparent px-3 pb-3 pt-12 text-white">
          <span className="release-shelf-caption text-xs font-medium leading-4">{caption}</span>
          {platforms.length > 0 && (
            <span className="mt-2 flex items-center gap-2 text-white/95" aria-hidden="true">
              {platforms.map((platform) => <PlatformBrandIcon key={platform} platform={platform} className="text-[14px]" />)}
            </span>
          )}
        </span>
      </button>
      <Tooltip content={t('release_shelf_details')}>
        <button
          type="button"
          onClick={() => onDetails(item)}
          className="absolute right-0 top-0 h-[44px] w-[44px] rounded-lg text-gray-700 hover:text-primary-700 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary-500"
          aria-label={t('release_shelf_details_for', { title: caption })}
        >
          <Icon name={ICONS.INFO} size="xs" className="absolute right-2 top-2 [text-shadow:0_1px_1px_rgba(255,255,255,0.9)]" ariaHidden />
        </button>
      </Tooltip>
    </div>
  );
};

const ShelfPopover = ({ item, onDetails }: { item: ReleasedShelfItem; onDetails: (item: ReleasedShelfItem) => void }) => {
  const { t } = useTranslation('calendar');
  const { t: tc } = useTranslation('common');
  const caption = item.variation?.caption || item.variation?.title || item.post.caption || t('release_shelf_untitled');
  const media = (item.variation?.postMedia?.length ? item.variation.postMedia : item.post.postMedia) ?? [];
  const firstMedia = media[0];
  const image = firstMedia?.type === 'VIDEO' ? firstMedia.thumbnailUrl : firstMedia?.thumbnailUrl || firstMedia?.originalUrl;

  return (
    <DashboardPostPopover
      className="flex max-h-[360px] flex-col"
      gradient="from-emerald-500 to-emerald-600"
      headerIcon={ICONS.RELEASE_STAMP}
      headerLabel={tc('release_status_released')}
      postMedia={media}
      caption={caption}
      thumbnail={
        <div className="relative h-28 w-full overflow-hidden rounded-xl bg-primary-50">
          {image ? <RetryImage src={image} alt="" className="h-full w-full object-cover" /> : <TextPostTile text={caption} emptyLabel={t('release_shelf_untitled')} />}
          {firstMedia?.type === 'VIDEO' && <span className="absolute inset-0 flex items-center justify-center"><VideoPlayBadge /></span>}
        </div>
      }
      footer={<button type="button" onClick={() => onDetails(item)} className="w-full rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-primary-600-contrast transition-colors hover:bg-primary-700 hover:text-primary-700-contrast">{t('release_shelf_details')}</button>}
    />
  );
};

interface ReleasedShelfProps {
  items: ReleasedShelfItem[];
  count: number;
  isLoading: boolean;
  celebrate: boolean;
  allowMotion: boolean;
  leavingKey: string | null;
  completion: boolean;
  onSchedule: (item: ReleasedShelfItem) => void;
  onDetails: (item: ReleasedShelfItem) => void;
  onDismiss: () => void;
}

export const ReleasedShelf = ({
  items,
  count,
  isLoading,
  celebrate,
  allowMotion,
  leavingKey,
  completion,
  onSchedule,
  onDetails,
  onDismiss,
}: ReleasedShelfProps) => {
  const { t } = useTranslation('calendar');
  const [activeHoverKey, setActiveHoverKey] = useState<string | null>(null);
  if (count <= 0 && !completion) return null;

  return (
    <section
      data-testid="released-shelf"
      aria-label={t('release_shelf_title')}
      className={cn('relative mb-3 overflow-hidden rounded-2xl border border-primary-200 bg-primary-25 px-3 py-2.5 shadow-sm sm:px-4', celebrate && 'release-shelf--arrive', allowMotion && 'release-shelf--motion-enabled')}
    >
      <div className="flex items-center gap-2 pr-12">
        <span className="release-shelf-spark relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700" aria-hidden="true">
          <Icon name={completion ? ICONS.CHECK_CIRCLE : ICONS.CALENDAR} size="sm" ariaHidden />
          {celebrate && !completion && <span className="release-shelf-spark__dots"><i /><i /><i /></span>}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold leading-5 text-gray-900">
            {completion ? t('release_shelf_complete_title') : t('release_shelf_title')}
          </h2>
          <p className="text-xs leading-4 text-gray-600">
            {completion ? t('release_shelf_complete_body') : t('release_shelf_count', { count })}
          </p>
        </div>
        <Tooltip content={t('release_shelf_dismiss')}>
          <button type="button" onClick={onDismiss} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center text-gray-500 before:absolute before:-inset-2 hover:text-gray-900 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500" aria-label={t('release_shelf_dismiss')}>
            <Icon name={ICONS.CLOSE} size="custom" className="text-[12px]" ariaHidden />
          </button>
        </Tooltip>
      </div>
      {!completion && (
        <>
          <div className="mt-2 overflow-x-auto pb-1 [touch-action:pan-x_pan-y] [scrollbar-width:thin]" aria-busy={isLoading}>
            <div className="flex w-max min-w-full gap-2">
              {isLoading && items.length === 0
                ? [0, 1, 2].map((i) => <div key={i} className="aspect-square w-[120px] shrink-0 animate-pulse rounded-xl bg-primary-100 sm:w-[190px]" />)
                : items.map((item, index) => {
                    const key = releaseItemKey(item.post.id, item.variation?.id ?? null);
                    return (
                      <HoverCard
                        key={key}
                        trigger={<div className="release-shelf-tile-wrap shrink-0" style={celebrate ? { animationDelay: `${index * 70}ms` } : undefined}><ShelfTile item={item} onSchedule={(selected) => { setActiveHoverKey(null); onSchedule(selected); }} onDetails={(selected) => { setActiveHoverKey(null); onDetails(selected); }} leaving={leavingKey === key} /></div>}
                        side="bottom"
                        align="start"
                        open={activeHoverKey === key && leavingKey !== key}
                        onOpenChange={(next) => setActiveHoverKey((current) => next ? key : current === key ? null : current)}
                        openDelay={0}
                        closeDelay={120}
                        className="release-shelf-popover"
                        allowTriggerPointerEvents
                      >
                        <ShelfPopover item={item} onDetails={(selected) => { setActiveHoverKey(null); onDetails(selected); }} />
                      </HoverCard>
                    );
                  })}
            </div>
          </div>
          <p className="mt-1 text-[11px] leading-4 text-gray-600">
            <span className="hidden lg:inline">{t('release_shelf_hint_drag')}</span>
            <span className="lg:hidden">{t('release_shelf_hint_tap')}</span>
          </p>
        </>
      )}
    </section>
  );
};
